import React from "react";
import {
  List,
  Datagrid,
  TextField,
  NumberField,
  BooleanField,
  Filter,
  TextInput,
  ListProps,
  FilterProps,
  FilterList,
  FilterListItem,
  useListContext,
  Button,
  useRecordContext,
  FieldProps,
} from "react-admin";
import { Box, Paper, Typography, Link, CardContent } from "@material-ui/core";
import { DateTimeField } from "lib/components/DateTime";
import { FilterSidebar } from "lib/components/FilterSidebar";
import FilterListIcon from "@material-ui/icons/FilterList";
import ClearAllIcon from "@material-ui/icons/ClearAll";
import { useEnums } from "lib/hooks";

const PlanManagementInfo = () => {
  console.log(
    "PLAN MANAGEMENT INFO",
    process.env.REACT_APP_CHARGEBEE_PLANS_URL
  );
  console.log(process.env.REACT_APP_API_DOMAIN);
  const chargebeeUrl = process.env.REACT_APP_CHARGEBEE_PLANS_URL;

  const revenuecatUrl = process.env.REACT_APP_REVENUECAT_ENTITLEMENTS_URL;

  return (
    <Box mb={2}>
      <Paper>
        <Box p={2}>
          <Typography variant="h6" gutterBottom>
            Plan Management
          </Typography>
          <Typography variant="body1" paragraph>
            Web plans can be managed on Chargebee:{" "}
            <Link href={chargebeeUrl} target="_blank" rel="noopener noreferrer">
              {chargebeeUrl}
            </Link>
          </Typography>
          <Typography variant="body1" paragraph>
            Mobile plans can be viewed on RevenueCat:{" "}
            <Link
              href={revenuecatUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {revenuecatUrl}
            </Link>
          </Typography>
          <Typography
            variant="body1"
            color="textSecondary"
            style={{ fontSize: "0.875rem" }}
          >
            Note: Mobile plans can only be created or edited in their respective
            platform consoles:
            <Box component="ul" ml={2} mt={1}>
              <li>iOS plans: App Store Connect</li>
              <li>Android plans: Google Play Console</li>
            </Box>
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

const PlanFilter = (props: Partial<FilterProps>): JSX.Element => (
  <Filter {...props}>
    <TextInput
      label="Name"
      source="name"
      alwaysOn
      style={{ minWidth: "340px" }}
    />
    <TextInput label="Country Code" source="countryIsoCode" alwaysOn />
    <TextInput label="Provider ID" source="providerId" alwaysOn />
  </Filter>
);

function PlansFilterSidebar() {
  const { enums } = useEnums();
  const { filterValues, setFilters } = useListContext();

  const handleClearAll = () => {
    setFilters({}, {});
  };

  const hasActiveFilters = Object.keys(filterValues).length > 0;

  return (
    <FilterSidebar>
      <CardContent>
        {hasActiveFilters && (
          <Box mb={2}>
            <Button
              label="Clear All Filters"
              onClick={handleClearAll}
              startIcon={<ClearAllIcon />}
            />
          </Box>
        )}
        <FilterList label="Display Status" icon={<FilterListIcon />}>
          <FilterListItem label="Displayed" value={{ isDisplayed: true }} />
          <FilterListItem label="Hidden" value={{ isDisplayed: false }} />
        </FilterList>
        {enums && (
          <FilterList label="Provider" icon={<FilterListIcon />}>
            {enums.subscriptionProvider?.map((provider: string) => (
              <FilterListItem
                label={provider}
                key={provider}
                value={{ providerName: provider }}
              />
            ))}
          </FilterList>
        )}
        {enums && (
          <FilterList label="Platform" icon={<FilterListIcon />}>
            {enums.platform?.map((platform: string) => (
              <FilterListItem
                label={platform}
                key={platform}
                value={{ platform }}
              />
            ))}
          </FilterList>
        )}
        {enums && (
          <FilterList label="Access Type" icon={<FilterListIcon />}>
            {enums.accessType?.map((accessType: string) => (
              <FilterListItem
                label={accessType}
                key={accessType}
                value={{ accessType }}
              />
            ))}
          </FilterList>
        )}
        {enums && (
          <FilterList label="Period Unit" icon={<FilterListIcon />}>
            {enums.billingPeriodUnit?.map((billingPeriodUnit: string) => (
              <FilterListItem
                label={billingPeriodUnit}
                key={billingPeriodUnit}
                value={{ billingPeriodUnit }}
              />
            ))}
          </FilterList>
        )}
      </CardContent>
    </FilterSidebar>
  );
}

const CountryCodesField: React.FC<FieldProps> = () => {
  const record = useRecordContext();
  if (!record || !record.countries) {
    return null;
  }

  const countryCodes = record.countries
    .map((country: { isoCode: string }) => country.isoCode)
    .join(", ");
  return <span>{countryCodes}</span>;
};

CountryCodesField.defaultProps = { label: "Country Codes" };

const PlanDatagrid = () => {
  return (
    <Datagrid rowClick="edit">
      {/* Basic Info */}
      <TextField source="name" />
      <TextField source="provider.name" label="Provider" />
      <CountryCodesField source="countries" label="Country Codes" />
      <TextField source="accessType" />
      <TextField source="periodUnit" />
      <TextField source="platform" />

      {/* Pricing */}
      <NumberField
        source="priceInCents"
        label="Price (cents)"
        options={{ minimumFractionDigits: 0 }}
      />
      <NumberField
        source="monthlyCostInCents"
        label="Monthly Cost (cents)"
        options={{ minimumFractionDigits: 0 }}
      />

      {/* Trial Info */}
      <NumberField source="trialPeriod" />
      <TextField source="trialPeriodUnit" />

      {/* Flags */}
      <BooleanField source="isDisplayed" />

      {/* Timestamps */}
      <DateTimeField source="createdAt" label="Created" />
      <DateTimeField source="updatedAt" label="Updated" />
    </Datagrid>
  );
};

export function PlanList(props: ListProps): JSX.Element {
  return (
    <>
      <PlanManagementInfo />
      <List
        {...props}
        perPage={30}
        bulkActionButtons={false}
        filters={<PlanFilter />}
        aside={<PlansFilterSidebar />}
        sort={{ field: "createdAt", order: "DESC" }}
      >
        <PlanDatagrid />
      </List>
    </>
  );
}
